import type { Observable } from 'rxjs';
import type { AppModules } from '../../appContainer/types';
import type { ParamDeclarations, Params } from './paramRegistry';

export type ChildProp = { children?: React.ReactNode };

// prettier-ignore
type Alphanumeric =
  | '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9'
  | 'a' | 'b' | 'c' | 'd' | 'e' | 'f' | 'g' | 'h' | 'i' | 'j' | 'k' | 'l' | 'm' | 'n' | 'o' | 'p' | 'q' | 'r' | 's' | 't' | 'u' | 'v' | 'w' | 'x' | 'y' | 'z'
  | 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I' | 'J' | 'K' | 'L' | 'M' | 'N' | 'O' | 'P' | 'Q' | 'R' | 'S' | 'T' | 'U' | 'V' | 'W' | 'X' | 'Y' | 'Z';
export type ParamPathSegment = `/:${keyof Params}`;
type ConstantPathSegment = '/' | `/${Alphanumeric}${string}`;
type LayoutSegment = { layout: true };
type IndexSegment = { index: true };

export type PathRouteComponent = (props: {
  children?: React.ReactNode;
}) => React.ReactNode;

export type LayoutRouteComponent = (props: {
  children: React.ReactNode;
}) => React.ReactNode;

export type IndexRouteComponent = (
  props: Record<string, unknown>
) => React.ReactNode;

export type DynamicRouteTitle = Promise<Observable<string>>;

export type RouteTitle = string | (() => DynamicRouteTitle);

export type RouteComponent =
  | PathRouteComponent
  | LayoutRouteComponent
  | IndexRouteComponent;

export type PathRouteContent = Promise<PathRouteComponent>;
export type LayoutRouteContent = Promise<LayoutRouteComponent>;
export type IndexRouteContent = Promise<IndexRouteComponent>;

export type RouteContent =
  | PathRouteContent
  | LayoutRouteContent
  | IndexRouteContent;

/**
 * A route that has moved, declared on the route it moved from.
 *
 * Resolved while matching, before any content loads, and validated against the
 * route tree by `RedirectsAreValid` in `redirectValidation.ts`.
 */
export type RouteRedirect = {
  /**
   * The route to redirect to, as an absolute route in the tree.
   *
   * @example
   * ```ts
   * '/workspace-manager/:workspaceId'
   * ```
   */
  readonly route: `/${string}`;

  /**
   * Params the target needs beyond the ones carried over from the matched
   * location. Overrides a carried param of the same name.
   */
  readonly params?: Partial<Params>;

  /**
   * Search params to add to the ones carried over from the matched location.
   * Overrides a carried search param of the same name.
   */
  readonly searchParams?: Readonly<Record<string, unknown>>;
};

type IndexNodeBase = {
  readonly path: IndexSegment;
  readonly children?: undefined;
  readonly notFound?: undefined;
};

export type IndexNode =
  | (IndexNodeBase & {
      readonly title?: RouteTitle;
      readonly content?: () => IndexRouteContent;
      readonly redirect?: undefined;
      readonly appModule?: AppModules;

      /**
       * Optional hidden parameters that this route supports.
       *
       * These are parameters stored in the browser history state, not visible in the URL.
       * They survive back/forward navigation but are lost on page reload or URL sharing.
       * Each parameter must be registered in the param registry.
       */
      readonly hiddenParams?: readonly (keyof Params)[];
    })
  | (IndexNodeBase & {
      readonly redirect: RouteRedirect;
      readonly content?: undefined;
      readonly title?: undefined;
      readonly appModule?: undefined;
      readonly hiddenParams?: undefined;
    });

export type LayoutNode = {
  readonly path: LayoutSegment;
  readonly title?: RouteTitle;
  readonly content?: () => LayoutRouteContent;
  readonly children: readonly RouteNode[];

  /**
   * Redirects resolve at the deepest active
   * route, which a layout never is.
   */
  readonly redirect?: undefined;

  /**
   * Content rendered in place of this route's children when the URL could not
   * be matched further down the tree.
   *
   * Resolution walks the active route chain from the deepest match upwards and
   * uses the first route that declares one, so the layouts above it are kept.
   */
  readonly notFound?: () => IndexRouteContent;

  readonly appModule?: AppModules;

  /**
   * Optional hidden parameters that this route supports.
   *
   * These are parameters stored in the browser history state, not visible in the URL.
   * They survive back/forward navigation but are lost on page reload or URL sharing.
   * Each parameter must be registered in the param registry.
   */
  readonly hiddenParams?: readonly (keyof Params)[];
};

export type PathNode = {
  /**
   * The path of the route.
   *
   * Can only be a single path segment, so it must start with a slash and
   * cannot contain any other slashes.
   *
   * If the second character is a colon, the path segment is interpreted as a
   * path parameter
   */
  readonly path: ParamPathSegment | ConstantPathSegment;
  /**
   * The title of the route, used for setting the document title when the route is active.
   *
   * If not provided, the title of the closest parent route with a title will be used.
   * If no route in the active route tree has a title, the document title will be Ardoq.
   */
  readonly title?: RouteTitle;

  readonly appModule?: AppModules;

  /**
   * Optional search parameters that this route supports.
   *
   * These are the query parameters that can be passed to this location in the
   * URL. Their handlers are owned by this route and its descendants.
   *
   * @example
   * ```ts
   * searchParams: { page: param({ fromString: Number, toString: String }) }
   * ```
   */
  readonly searchParams?: ParamDeclarations;

  /**
   * Optional hidden parameters that this route supports.
   *
   * These are parameters stored in the browser history state, not visible in the URL.
   * They survive back/forward navigation but are lost on page reload or URL sharing.
   * Each parameter must be registered in the param registry.
   */
  readonly hiddenParams?: readonly (keyof Params)[];

  /**
   * The content of the route.
   *
   * The content should be lazily imported to avoid cyclic dependency between the route tree, routeState$ and the content.
   * The content can be empty, in which case child routes will be rendered directly.
   *
   * Example:
   * ```ts
   * content: async () => (await import('./ExampleRoot')).default,
   * ```
   */
  readonly content?: () => PathRouteContent;

  readonly children?: readonly RouteNode[];

  /**
   * Content rendered in place of this route's children when the URL could not
   * be matched further down the tree.
   *
   * Resolution walks the active route chain from the deepest match upwards and
   * uses the first route that declares one, so the layouts above it are kept.
   */
  readonly notFound?: () => IndexRouteContent;

  /**
   * The route this one moved to.
   *
   * Applies to this exact route only, so children still match and a deeper URL
   * that matches nothing still falls through to `notFound`. Coexists with
   * `content`, which keeps rendering as the layout around a matched child.
   */
  readonly redirect?: RouteRedirect;
};

export type RouteNode = IndexNode | LayoutNode | PathNode;

export const isParamNode = (
  node: RouteNode
): node is RouteNode & { path: ParamPathSegment } =>
  typeof node.path === 'string' && node.path.startsWith('/:');

export const isLayoutNode = (
  node: RouteNode
): node is RouteNode & { path: { layout: true } } =>
  typeof node.path === 'object' && 'layout' in node.path && node.path.layout;

export const isIndexNode = (node: RouteNode): node is IndexNode =>
  typeof node.path === 'object' && 'index' in node.path && node.path.index;
